import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography
} from '@mui/material';
import apiClient from '../utils/apiClient';
import { useToast } from '../contexts/ToastContext.tsx';

interface User {
  username: string;
  robloxName: string;
  role: string;
  email?: string;
}

interface AccountDialogProps {
  open: boolean;
  onClose: () => void;
  user: User | null;
  onLogout: () => void;
}

const AccountDialog: React.FC<AccountDialogProps> = ({
  open,
  onClose,
  user,
  onLogout
}) => {
  const { showSuccess, showError } = useToast();
  
  // Password change state
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [logoutDialog, setLogoutDialog] = useState(false);

  // Handle password change
  const handlePasswordChange = async () => {
    if (newPassword !== confirmPassword) {
      showError('Passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      showError('Password must be at least 6 characters long');
      return;
    }

    setPasswordLoading(true);
    try {
      const response = await apiClient.post('/auth/change-password', {
        oldPassword,
        newPassword
      });

      const data = response.data;
      if (data.success) {
        showSuccess('Password changed successfully');
        setShowPasswordDialog(false);
        setOldPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        const errorMsg = typeof data.error === 'string' ? data.error : 'Failed to change password';
        showError(errorMsg);
      }
    } catch (error) {
      showError('Failed to change password');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleLogoutConfirm = () => {
    setLogoutDialog(false);
    onLogout();
  };

  return (
    <>
      {/* Account Management Dialog */}
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Account Management</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" gutterBottom>Account Information</Typography>
            <Typography variant="body2" sx={{ color: '#b4b9c0' }}>
              <strong>Username:</strong> {user?.username}
            </Typography>
            <Typography variant="body2" sx={{ color: '#b4b9c0' }}>
              <strong>Roblox Name:</strong> {user?.robloxName}
            </Typography>
            <Typography variant="body2" sx={{ color: '#b4b9c0' }}>
              <strong>Role:</strong> {user?.role}
            </Typography>
            {user?.email && (
              <Typography variant="body2" sx={{ color: '#b4b9c0' }}>
                <strong>Email:</strong> {user.email}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPasswordDialog(true)} variant="outlined">
            Change Password
          </Button>
          <Button onClick={() => setLogoutDialog(true)} variant="outlined" color="error">
            Logout
          </Button>
          <Button onClick={onClose} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Password Change Dialog */}
      <Dialog open={showPasswordDialog} onClose={() => setShowPasswordDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Current Password"
            type="password"
            value={oldPassword}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setOldPassword(e.target.value)}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="New Password"
            type="password"
            value={newPassword}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewPassword(e.target.value)}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Confirm New Password"
            type="password"
            value={confirmPassword}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setConfirmPassword(e.target.value)}
            margin="normal"
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPasswordDialog(false)}>Cancel</Button>
          <Button onClick={handlePasswordChange} variant="contained" disabled={passwordLoading}>
            {passwordLoading ? 'Changing...' : 'Change Password'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Logout Confirmation Dialog */}
      <Dialog open={logoutDialog} onClose={() => setLogoutDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: '#eceef1' }}>Confirm Logout</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#b4b9c0', mb: 2 }}>
            Are you sure you want to logout? This will close any open radio windows.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogoutDialog(false)} sx={{ color: '#b4b9c0' }}>
            Cancel
          </Button>
          <Button 
            onClick={handleLogoutConfirm}
            variant="contained" 
            color="error"
            sx={{
              backgroundColor: '#dc5050',
              '&:hover': {
                backgroundColor: '#b84141'
              }
            }}
          >
            Logout
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AccountDialog;